import { Audit, Dto } from '@damofire/data-commons';
import type { SheetManifest } from './survey-storage';
import type { SurveyCompanyReference } from './hubspot-company.model';

export interface HubspotContactField {
  hubspotId: string | null;
  name: string;
}

export function normalizeContactField(value: HubspotContactField | string): HubspotContactField {
  if (!value) {
    return { hubspotId: null, name: '' };
  }
  if (typeof value === 'string') {
    return { hubspotId: null, name: value };
  }
  return value;
}

export interface ContactInfoData {
  surveyName: string;
  dateOfSurvey: string | null;
  distributor: HubspotContactField;
  surveyor: HubspotContactField;
  salesRep: HubspotContactField;
  company?: SurveyCompanyReference | null;
  businessName: string;
  surveyType: string;
  contactPerson: string;
  surveyTime: string;
  phone: string;
  mobile: string;
  email: string;
  surveyAddress: string;
  city: string;
  stateProvince: string;
  zipPostalCode: string;
  sqFt: string;
  fiftyPlusSurvey: boolean;
  scissorLiftNeeded: boolean;
  laborNeeded: boolean;
  notes: string;
  installerNotes: string;
}

export interface MeasurementData {
  isCanadianRackStyle?: boolean;
  rackStyle: string;
  damoColor: string;
  eliteColor: string;
  advColor: string;
  handAttachment: string;
  handAttachmentV: string;
  handAttachmentW: string;
  handAttachmentX: string;
  handAttachmentY: string;
  dimA: string;
  dimB: string;
  dimC: string;
  dimD: string;
  dimE: string;
  dimF1: string;
  dimF2: string;
  dimG: string;
  dimH: string;
  dimI: string;
  dimJ1: string;
  dimJ2: string;
  dimK: string;
  dimL: string;
  dimM: string;
  dimN: string;
  dimO: string;
  dimP: string;
  dimQ: string;
  dimR: string;
  advantageIR: boolean;
  advantageNPD: boolean;
  beamLeftA: number;
  beamRightA: number;
  beamLeftB: number;
  beamRightB: number;
  eliteU: string;
  eliteV: string;
  eliteY: string;
  braceHType: string;
  braceHWidth: string;
  braceHHeight: string;
  braceHLength: string;
  braceDType: string;
  braceDWidth: string;
  braceDHeight: string;
  braceDLength: string;
  basePlateSelection: string;
  braceStyle: string;
  guardBaseplateType: string;
  guardNoBullnoseFront: string;
  guardNoBullnoseBack: string;
  guardQuantity: string;
  guardSize: string;
  guardSizeCustomValue: string;
  guardNotes: string;
  braceFormQty1: string;
  braceFormQty2: string;
  braceFormQty3: string;
  braceFormQty4: string;
  braceFormQty5: string;
  braceFormNotes: string;
}

export interface FootplateData {
  footplateType: string;
  footplateS1DimA: string;
  footplateS1DimB: string;
  footplateS2Option: string;
  footplateS2DimA: string;
  footplateS2DimB: string;
  footplateS2DimC: string;
  footplateS2DimD: string;
  footplateS2DimE: string;
  footplateS2DimF: string;
  footplateS2DimG1: string;
  footplateS2DimG2: string;
  footplateS2DimK: string;
  footplateS2DimL: string;
  footplateBraceH: string;
  footplateBraceJ: string;
  concrete2A: string;
  concrete2B: string;
  concrete2C: string;
  concreteDimM: string;
  concreteDimN: string;
  concreteDimP: string;
}

export type UploadStatus = 'offline' | 'uploading' | 'online' | 'failed';
export type SurveySheetType = 'measurement' | 'footplate';

export interface SurveyPhoto {
  filepath: string;
  filename: string;
  createdAt: number;

  imageFolderPath?: string;
  original?: string;
  web?: string;
  thumb?: string;

  size?: number;
  uploadStatus?: UploadStatus;
  annotations?: unknown[];
  annotationsDirty?: boolean;
}

export interface LocationEntry {
  createdAt?: number;
  environment: string;
  driveInRackLocations: string;
  endAisle: boolean;
  priority: string;
  clientLocation: string;
  damageHeight: string;
  seatHeight: string;
  productLine: string;
  legStyle: string;
  slantCantilever: string;
  qty: string;
  hookOversLHO: string;
  hookOversRHO: string;
  selfTappers: string;
  beamHeightKl: string;
  beamHeightMn: string;
  beamHeightOp: string;
  beamHeightQr: string;
  beamHeight3rdClip: string;
  beamHeight4thClip: string;
  adjustableBraceLvl1: string;
  adjustableBraceLvl2: string;
  adjustableBraceLvl3: string;
  adjustableBraceLvl4: string;
  adjustableBraceLvl5: string;
  notes: string;
  photos?: SurveyPhoto[];
}

export interface LocationsData {
  pageCount?: number;
  pageBreaks?: number[];
  environment: string;
  baseplate: string;
  rackStyle: string;
  sheetNumber: number;
  officeOnlyA: string;
  officeOnlyGh12: string;
  officeOnlyGhi12: string;
  officeOnlyC: string;
  officeOnlyK10: string;
  officeOnlyLA5: string;
  officeOnlyD: string;
  officeOnlyM10: string;
  officeOnlyNA5: string;
  officeOnlyE: string;
  officeOnlyO10: string;
  officeOnlyGH: string;
  officeOnlyQ10: string;
  officeOnlyJ1: string;
  officeOnlyK: string;
  officeOnlyPA5: string;
  officeOnlyJ2: string;
  officeOnlyM: string;
  officeOnlyRA5: string;
  locations: Partial<LocationEntry>[];
}

/**
 * Single source of truth for the location "Priority" dropdown vocabulary.
 *
 * Renamed from the legacy terms (`Critical Damage` / `Damage`) to
 * (`Urgent Repair` / `Repair`). Stored values are plain strings in IndexedDB
 * and Firestore, so every read/write path must go through
 * {@link normalizeLocationPriority} to keep legacy surveys readable.
 */
export const LOCATION_PRIORITY_URGENT_REPAIR = 'Urgent Repair' as const;
export const LOCATION_PRIORITY_REPAIR = 'Repair' as const;
export const LOCATION_PRIORITY_OPTIONS: readonly string[] = ['', LOCATION_PRIORITY_URGENT_REPAIR, LOCATION_PRIORITY_REPAIR];

/** Legacy stored values mapped to their current equivalent. */
const LEGACY_LOCATION_PRIORITY_MAP: Record<string, string> = {
  'Critical Damage': LOCATION_PRIORITY_URGENT_REPAIR,
  Damage: LOCATION_PRIORITY_REPAIR,
};

/**
 * Maps a stored priority value to the current vocabulary.
 * Unknown values pass through untouched so custom data is never destroyed.
 */
export function normalizeLocationPriority(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return LEGACY_LOCATION_PRIORITY_MAP[value] ?? value;
}

/**
 * Normalizes the priority of every row in a locations payload in place.
 * @returns true if at least one row was rewritten.
 */
export function normalizeLocationsDataPriorities(locations: Partial<LocationsData> | undefined): boolean {
  if (!locations?.locations) {
    return false;
  }
  let changed = false;
  for (const row of locations.locations) {
    if (!row || typeof row.priority !== 'string') {
      continue;
    }
    const normalized = normalizeLocationPriority(row.priority);
    if (normalized !== row.priority) {
      row.priority = normalized;
      changed = true;
    }
  }
  return changed;
}

/**
 * Migrates every measurement of a survey to the current priority vocabulary.
 * In-memory only; callers decide when to persist (next save converges).
 * @returns true if at least one row was rewritten.
 */
export function migrateSurveyLocationPriorities(survey: Survey): boolean {
  let changed = false;
  for (const measurement of Object.values(survey.measurements ?? {})) {
    if (normalizeLocationsDataPriorities(measurement?.locationData)) {
      changed = true;
    }
  }
  return changed;
}

interface MeasurementEntryBase {
  index: number;
  name: string;
  locationData?: Partial<LocationsData>;
}

export interface StandardMeasurementEntry extends MeasurementEntryBase {
  sheetType: 'measurement';
  data: Partial<MeasurementData>;
}

export interface FootplateMeasurementEntry extends MeasurementEntryBase {
  sheetType: 'footplate';
  data: Partial<FootplateData>;
}

export type MeasurementEntry = StandardMeasurementEntry | FootplateMeasurementEntry;

export type SurveyStatus = 'local' | 'savedOnline' | 'publishedToHubSpot';

export interface QuoteProduct {
  product: string;
  description?: string;
  internalid?: string;
  quantity: number;
}

export type SyncChoice = 'local' | 'remote';

export interface SurveySyncState {
  remoteUpdatedAt: number;
  choice: SyncChoice | null;
}

export interface Survey extends Dto {
  _id?: string;
  id: string;
  quoteNumber?: string;
  audit?: Audit;
  contactInfo: ContactInfoData | null;
  measurements: Record<number, MeasurementEntry>;
  version?: number;
  sheets?: SheetManifest;
  quoteProducts?: QuoteProduct[];
  installerPhotos?: SurveyPhoto[];
  status?: SurveyStatus;
  updatedAt: number;
  lastPublishedDate?: number;
  hubspotPublishedAt?: number;
  hubspotTicketId?: string;
  exportInProgress?: boolean;
  exportUrl?: string;
  syncState?: SurveySyncState;
}
